import {
  Component, ElementRef, forwardRef, HostBinding, Input, OnChanges, OnInit, SimpleChanges, ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Option } from 'src/app/settings/model/value-field';
import { ColumnFilterService } from 'src/app/shared/service/column-filter.service';
import { buildOptionSearchIndex, EMPTY_VALUE_OPTION_KEY, OPTION_SEARCH_THRESHOLD } from '../option-search';

export type ColumnFilterMode = 'text' | 'list';

/**
 * Kontrolka wartości filtru kolumnowego w dwóch trybach: wpisywany tekst (dopasowanie po
 * zawieraniu) albo wybór wielu wartości z listy. Tryby są rozłączne - ikona po prawej przełącza
 * między nimi i czyści poprzednią wartość, więc kolumna filtruje albo tekstem, albo listą.
 *
 * Lista bierze się albo ze słownika pola (ValueField.options), albo - dla kolumn oflagowanych
 * ValueField.filterOptionsFromData - z unikalnych wartości w danych tabeli; te drugie liczone są
 * dopiero przy otwarciu listy. Do jednej i do drugiej dokleja się opcjonalna pozycja "bez wartości"
 * (ValueField.filterEmptyOptionLabel).
 */
@Component({
  selector: 'app-column-filter-value',
  templateUrl: './filter-value.component.html',
  styleUrls: ['./filter-value.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ColumnFilterValueComponent),
    multi: true
  }]
})
export class ColumnFilterValueComponent implements ControlValueAccessor, OnInit, OnChanges {

  // Panel mat-selecta nie ma wirtualizacji, a pozycja to nie sam checkbox z tekstem: komponent
  // opcji, pseudo-checkbox i komplet host-bindingów Materiala. Zmierzone (headless Chrome, po
  // wyłączeniu ripple) ok. 0,28 ms na pozycję, czyli tysiąc pozycji to jakieś 280 ms budowy przy
  // pierwszym sięgnięciu do listy. Dlatego renderujemy porcjami, a kolejne dokładamy przy
  // dojechaniu listy do końca.
  static readonly RENDER_CHUNK = 1000;

  // Ile pozycji dokładamy, zanim przewijanie dobije do samego dołu - żeby lista rosła płynnie.
  private static readonly SCROLL_MARGIN_PX = 200;

  // Ile etykiet wypisujemy w zwiniętym polu. Szerokość kolumny i tak utnie je wcześniej, a bez
  // limitu przy setkach zaznaczeń budowalibyśmy niewidoczny tekst na zapas.
  static readonly TRIGGER_LABEL_LIMIT = 20;

  /** Nazwa kolumny - po niej tabela buduje listę wartości z danych. */
  @Input() name: string;
  /** Słownik pola; gdy pusty, lista budowana jest z danych tabeli. */
  @Input() options: Option[] | null = null;
  /**
   * Klucz tłumaczenia pozycji "bez wartości" doklejanej na początek listy - szuka się jej najczęściej,
   * a doklejona na końcu ginęłaby pod tysiącem wartości z danych. Przekazujemy sam klucz, a nie gotową
   * opcję: napis jest stabilny między cyklami detekcji zmian, więc wejście nie zmienia się w kółko.
   */
  @Input() emptyOptionLabel: string | null = null;
  /** Czy kolumna ma też tryb tekstowy (a więc i przełącznik trybu). */
  @Input() allowText = false;

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @ViewChild('panelContent') panelContent: ElementRef<HTMLElement>;

  mode: ColumnFilterMode = null;
  text = '';
  // Brak filtru w trybie listy znaczy "wszystko wybrane" - wtedy values jest puste, a stan niesie
  // ta flaga. Pusta lista przy allSelected === false to świadome "nic" i taki filtr nie przepuszcza
  // żadnego wiersza.
  allSelected = true;
  values: any[] = [];
  selectionValue: any[] = [];
  disabled = false;

  search = '';
  // Podgląd samego wyboru: lista zawęża się do zaznaczonych, żeby dało się je przejrzeć bez
  // przewijania całości. Stan chwilowy - gaśnie razem z zamknięciem panelu.
  showSelectedOnly = false;
  matchingCount = 0;
  renderedOptions: Option[] = [];
  // Etykiety wybranych wartości do pokazania w zwiniętym polu; przeliczane przy zmianie wyboru albo
  // listy opcji, a nie w getterze - szablon sięga po nie przy każdym cyklu detekcji zmian.
  selectedLabels: Option[] = [];

  // Ile z wyniku szukajki zmieściło się na liście - reszta dochodzi przy przewijaniu.
  visibleMatchingCount = 0;

  // Ile pozycji wyniku szukajki mamy w tej chwili wyrenderowanych; rośnie przy przewijaniu.
  private renderLimit = ColumnFilterValueComponent.RENDER_CHUNK;
  private allOptions: Option[] = [];
  // Cały wynik szukajki (bez limitu renderowania) - to po nim działa zaznacz/odznacz wszystkie.
  private matchingOptions: Option[] = [];
  // Tylko na czas jednego otwarcia listy - po zamknięciu pytamy tabelę od nowa, bo dane (a z nimi
  // zestaw wartości) mogły się w międzyczasie zmienić.
  private dataOptionsLoaded = false;
  private searchTextByKey = new Map<any, string>();
  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(private columnFilterService: ColumnFilterService, private translate: TranslateService) { }

  // Z przełącznikiem trybu strzałka mat-selecta byłaby drugą ikoną w wąskiej komórce nagłówka -
  // rolę "tu jest lista" przejmuje wtedy ikona trybu (patrz styl komponentu).
  @HostBinding('class.with-mode-toggle')
  get hasModeToggle(): boolean {
    return this.allowText;
  }

  ngOnInit(): void {
    // writeValue() potrafi ustawić tryb wcześniej (gdy filtr ma już wartość) - wtedy zostaje.
    if (!this.mode) this.mode = this.allowText ? 'text' : 'list';
    // Kontrolka odtworzona z aktywnym filtrem listy potrzebuje etykiet od razu, bez czekania na
    // otwarcie panelu - inaczej w polu widać sam licznik, bez nazwy wybranej wartości.
    if (this.values.length) this.ensureOptionsLoaded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options && this.options) this.loadOptions(this.options);
  }

  writeValue(value: any): void {
    if (Array.isArray(value)) {
      this.allSelected = false;
      this.values = [...value];
      this.text = '';
      this.mode = 'list';
      this.refreshSelectionView();
      this.ensureOptionsLoaded();
      return;
    }
    // Brak wartości w trybie listy to "wszystko wybrane" - filtr nie zawęża niczego i nie nosi
    // przy sobie kompletu wartości, więc nie dezaktualizuje się, gdy w danych przybędzie nowych.
    this.allSelected = true;
    this.values = [];
    this.text = value == null ? '' : String(value);
    this.refreshSelectionView();
    // Pusta wartość nie przestawia trybu - inaczej wyczyszczenie filtra wyrzucałoby użytkownika
    // z listy z powrotem do pola tekstowego.
    if (!this.allowText) this.mode = 'list';
    else if (this.text) this.mode = 'text';
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get showSearch(): boolean {
    return this.allOptions.length > OPTION_SEARCH_THRESHOLD;
  }

  get hiddenCount(): number {
    return this.matchingCount - this.visibleMatchingCount;
  }

  get totalCount(): number {
    return this.allOptions.length;
  }

  /** Ile wartości filtr przepuszcza - w stanie "wszystko" to cała lista, choć nie ma jej w values. */
  get selectedCount(): number {
    return this.allSelected ? this.allOptions.length : this.values.length;
  }

  // Póki lista nie jest policzona, w stanie "wszystko" nie ma czego liczyć - licznik zostaje ukryty.
  get showCount(): boolean {
    return !this.allSelected || this.allOptions.length > 0;
  }

  get countTooltip(): string {
    if (this.allSelected) return 'column-filter.all-selected';
    return this.allOptions.length ? 'column-filter.selected-count-of' : 'column-filter.selected-count';
  }

  get allMatchingSelected(): boolean {
    if (this.allSelected) return true;
    const toggleable = this.toggleableOptions;
    return toggleable.length > 0 && toggleable.every(o => this.values.includes(o.key));
  }

  get someMatchingSelected(): boolean {
    return this.allSelected || this.toggleableOptions.some(o => this.values.includes(o.key));
  }

  toggleMode(event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    this.mode = this.mode === 'text' ? 'list' : 'text';
    // Tryby są rozłączne, więc przełączenie kasuje to, co filtrowało do tej pory.
    this.text = '';
    this.allSelected = true;
    this.values = [];
    this.refreshSelectionView();
    this.onChange(null);
  }

  onTextChange(text: string): void {
    this.text = text;
    this.onChange(text === '' ? null : text);
  }

  // mat-select buduje emitowaną wartość z aktualnie wyrenderowanych opcji, więc po zawężeniu listy
  // szukajką doklejamy z powrotem zaznaczenia spoza widocznego zestawu.
  onSelectionChange(selected: any[]): void {
    const renderedKeys = new Set(this.renderedOptions.map(o => o.key));
    const keptOutside = this.currentSelection.filter(v => !renderedKeys.has(v));
    this.applySelection([...keptOutside, ...(selected ?? [])]);
  }

  /**
   * Zaznacz/odznacz obejmuje cały wynik szukajki, a nie tylko ten jego kawałek, który zmieścił się
   * na liście - inaczej przy długiej liście "odznacz wszystko" zostawiałoby zaznaczone wszystko,
   * czego nie widać. Zaznaczenia spoza wyniku szukajki zostają nietknięte.
   */
  toggleAllMatching(): void {
    const keys = new Set(this.toggleableOptions.map(o => o.key));
    const current = this.currentSelection;
    this.applySelection(this.allMatchingSelected
      ? current.filter(v => !keys.has(v))
      : [...current, ...[...keys].filter(k => !current.includes(k))]);
  }

  /**
   * Lista z danych liczona jest dopiero na pierwsze sięgnięcie do kontrolki (najazd klawiaturą albo
   * wciśnięcie myszy) - dzięki temu panel jest już gotowy w chwili otwarcia i mat-select liczy jego
   * wysokość na właściwej zawartości.
   */
  ensureOptionsLoaded(): void {
    if (this.options || this.dataOptionsLoaded || !this.name) return;
    this.dataOptionsLoaded = true;
    this.loadOptions(this.columnFilterService.getDataOptions(this.name));
  }

  onOpenedChange(opened: boolean): void {
    if (!opened) {
      this.showSelectedOnly = false;
      this.onSearchChange('');
      this.dataOptionsLoaded = false;
      this.onTouched();
      return;
    }
    this.renderLimit = ColumnFilterValueComponent.RENDER_CHUNK;
    this.ensureOptionsLoaded();
    if (this.showSearch) setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  toggleShowSelectedOnly(): void {
    this.showSelectedOnly = !this.showSelectedOnly;
    this.renderLimit = ColumnFilterValueComponent.RENDER_CHUNK;
    this.applySearch();
    if (this.panelContent) this.panelContent.nativeElement.scrollTop = 0;
  }

  // PPM w komórce filtra czyści filtr kolumny, ale w rozwiniętym panelu byłaby to zbyt łatwa
  // pomyłka - tutaj działa tylko w polu szukaj, jako skrót do krzyżyka.
  onSearchContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.onSearchChange('');
  }

  onSearchChange(search: string): void {
    this.search = search ?? '';
    this.renderLimit = ColumnFilterValueComponent.RENDER_CHUNK;
    this.applySearch();
    // Nowy wynik czyta się od góry, a przy okazji zjechana na dół lista po skróceniu sama dobiłaby
    // do końca i od razu dołożyła kolejną porcję.
    if (this.panelContent) this.panelContent.nativeElement.scrollTop = 0;
  }

  /**
   * Lista rośnie porcjami w miarę przewijania - dzięki temu otwarcie kolumny z tysiącami wartości
   * kosztuje tyle co pierwsza porcja, a nie budowa całej listy.
   */
  onPanelScroll(event: Event): void {
    if (this.hiddenCount <= 0) return;
    const panel = event.target as HTMLElement;
    const distanceToEnd = panel.scrollHeight - panel.scrollTop - panel.clientHeight;
    if (distanceToEnd > ColumnFilterValueComponent.SCROLL_MARGIN_PX) return;
    this.renderLimit += ColumnFilterValueComponent.RENDER_CHUNK;
    this.applySearch();
  }

  // Panel mat-selecta sam obsługuje klawiaturę, więc znaki wpisywane w "szukaj" muszą się przy nim
  // zatrzymać (inaczej litery i spacja trafiają w typeahead i zaznaczają opcje). Nawigacja po
  // liście, wybór i Escape idą dalej - pierwszy Escape czyści szukajkę, dopiero kolejny zamyka panel.
  onSearchKeydown(event: KeyboardEvent): void {
    if (ColumnFilterValueComponent.PANEL_KEYS.includes(event.key)) return;
    if (event.key === 'Escape') {
      if (!this.search) return;
      this.onSearchChange('');
    }
    event.stopPropagation();
  }

  private static readonly PANEL_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', 'Tab'];

  // "Zaznacz/odznacz wszystkie" dotyczy wyniku szukajki, a nie doklejonych do listy zaznaczeń
  // spoza niego - inaczej odznaczenie przy aktywnej szukajce kasowałoby też to, czego szukajka
  // odsiała.
  private get toggleableOptions(): Option[] {
    return this.matchingOptions.filter(o => !o.disabled);
  }

  private loadOptions(options: Option[]): void {
    this.allOptions = this.emptyOptionLabel
      ? [{ key: EMPTY_VALUE_OPTION_KEY, value: this.emptyOptionLabel }, ...(options ?? [])]
      : options ?? [];
    this.searchTextByKey = buildOptionSearchIndex(this.allOptions, this.translate);
    this.refreshSelectionView();
  }

  /** Bieżący wybór rozpisany na wartości - stan "wszystko" nie trzyma ich u siebie. */
  private get currentSelection(): any[] {
    return this.allSelected ? this.allOptions.map(o => o.key) : this.values;
  }

  private refreshSelectionView(): void {
    this.updateSelectedLabels();
    this.applySearch();
  }

  /**
   * Zapisuje wybór po zmianie w panelu. Wybór obejmujący wszystkie znane wartości zapisujemy jako
   * brak filtru (null) - dzięki temu kolumna nie nosi kompletu wartości i nie zaczyna nagle
   * odcinać wierszy, gdy w danych pojawi się coś nowego. Pusty wybór zostaje pustą listą, bo to
   * świadome "nic".
   */
  private applySelection(values: any[]): void {
    const selected = new Set(values);
    this.allSelected = this.allOptions.length > 0 && this.allOptions.every(o => selected.has(o.key));
    this.values = this.allSelected ? [] : values;
    this.refreshSelectionView();
    this.onChange(this.allSelected ? null : this.values);
  }

  /**
   * Etykiety wybranych wartości - tyle, żeby wypełnić pole i się urwać (resztę zdejmuje ellipsis, a
   * ile jest naprawdę, mówi licznik obok). Wartość, dla której nie znamy jeszcze opcji, pokazujemy
   * surową - inaczej pole wyglądałoby na puste mimo działającego filtra.
   */
  private updateSelectedLabels(): void {
    if (!this.values.length) {
      this.selectedLabels = [];
      return;
    }
    const optionByKey = new Map(this.allOptions.map(o => [o.key, o]));
    this.selectedLabels = this.values
      .slice(0, ColumnFilterValueComponent.TRIGGER_LABEL_LIMIT)
      .map(value => optionByKey.get(value) ?? { key: value, value: String(value) });
  }

  /**
   * Dzieli opcje na trzy zestawy: cały wynik szukajki (po nim działa zaznacz/odznacz), jego
   * początek mieszczący się w limicie renderowania i doklejone do niego zaznaczenia spoza wyniku.
   * Te ostatnie muszą być na liście, bo mat-select buduje z wyrenderowanych opcji model
   * zaznaczenia, a od niego zależy i emitowana wartość, i to, czy zwinięte pole pokaże etykiety.
   */
  /**
   * Zaznaczenia, których nie ma wśród wyrenderowanych pozycji, dokładamy na początek listy - inaczej
   * mat-select nie miałby z czego zbudować modelu zaznaczenia (a od niego zależy emitowana wartość
   * i etykiety w zwiniętym polu) i nie dałoby się ich odznaczyć.
   *
   * Przy aktywnej szukajce tego nie robimy: lista ma pokazywać wynik wyszukiwania, a nie doklejone
   * do niego zaznaczenia niepasujące do frazy. Nie gubimy ich - dokłada je z powrotem
   * onSelectionChange, a po zamknięciu panelu szukajka i tak się czyści.
   */
  private selectedOptionsInOrder(): Option[] {
    const selected = new Set(this.currentSelection);
    return this.allOptions.filter(option => selected.has(option.key));
  }

  private selectedOutsideOptions(visibleMatching: Option[]): Option[] {
    // W podglądzie samego wyboru lista i tak składa się z zaznaczeń - nie ma czego doklejać.
    if (this.showSelectedOnly || this.search.trim() !== '') return [];
    const visibleKeys = new Set(visibleMatching.map(o => o.key));
    const optionByKey = new Map(this.allOptions.map(o => [o.key, o]));
    return this.values
      .filter(value => !visibleKeys.has(value))
      .slice(0, ColumnFilterValueComponent.RENDER_CHUNK)
      .map(value => optionByKey.get(value) ?? { key: value, value: String(value) });
  }

  private applySearch(): void {
    const term = this.search.trim().toLowerCase();
    const base = this.showSelectedOnly ? this.selectedOptionsInOrder() : this.allOptions;
    this.matchingOptions = term
      ? base.filter(o => this.searchTextByKey.get(o.key)?.includes(term))
      : base;
    this.matchingCount = this.matchingOptions.length;

    const visibleMatching = this.matchingOptions.slice(0, this.renderLimit);
    this.visibleMatchingCount = visibleMatching.length;
    this.renderedOptions = [...this.selectedOutsideOptions(visibleMatching), ...visibleMatching];
    // Zaznaczenia w panelu bierze mat-select z tej wartości, a w stanie "wszystko" wynika ona z
    // tego, co wyrenderowane - musi się więc odświeżyć razem z listą, także przy dokładaniu porcji
    // przewijaniem. Kompletu kluczy nie budujemy: pozycje spoza listy nie mają czego odhaczyć.
    this.selectionValue = this.allSelected ? this.renderedOptions.map(o => o.key) : this.values;
  }

}
